import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { checkEmailAddress } from '../utilities/helpers';
import {
  createNewAccount,
  updateAccount,
  deleteAccount,
  associateRolesWithAccount,
} from '../store/actions/account-actions';

import LoadingContainer from '../components/LoadingContainer';
import SVGIcon from '../components/SVGIcon';
import Button from '../components/Button';
import UserAccountsEditRow from '../components/UserAccountsEditRow';

const UserAccounts = props => {

  const dispatch = useDispatch();
  // data from redux store
  const filteredAccounts = useSelector(state =>
    state.account.userAccountsDataLoaded && state.account.accountOverviewData.accounts
      ? state.account.accountOverviewData.accounts.filter(item => item.username !== state.account.accountOverviewData.account.username)
      : []
  );
  const clientName = useSelector(state => state.client.name);
  const clientId= useSelector(state => state.client.id);
  const loading = useSelector(state => !state.account.userAccountsDataLoaded);
  const accounts = filteredAccounts;
  const accountEmailAddressesInUse = filteredAccounts.map(item => item.username);

  // state

  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [editedEmailAddress, setEditedEmailAddress] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [addRow, setAddRowOpen] = useState(false);
  const [editedIsAdmin, setEditedIsAdmin] = useState(false);
  const [emailAddressValid, setEmailAddressValid] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [accountToDeleteIndex, setAccountToDeleteIndex] = useState(null);

  const handleAddUserButton = () => {
    setAddRowOpen(true);
    setEditRow(null)
    setEditedFirstName('')
    setEditedLastName('')
    setEditedEmailAddress('')
    setEditedIsAdmin(false)
    setEmailAddressValid(true)
  };

  const handleEmailOnChange = (e) => {
      setEditedEmailAddress(e.target.value);
      setEmailAddressValid(checkEmailAddress(editedEmailAddress))
  }


  const handleCancelButton = (e) => {
    setEditRow(null)
    setAddRowOpen(false)
  }

  const handleDeleteButton = index => {
    setAccountToDeleteIndex(index);
    setModalOpen(true)
  };


  const handleSaveAddUserButton = () => {
    setAddRowOpen(false)
    if (
      editedFirstName !== '' &&
      editedLastName !== '' &&
      checkEmailAddress(editedEmailAddress) === true
    ) {
      const roles = [];
      if (editedIsAdmin === true) {
        roles.push('Admin')
      } else {
        roles.push('User')
      }

      dispatch(createNewAccount({
        username: editedEmailAddress,
        user: {
          firstName: editedFirstName,
          lastName: editedLastName,
        },
        roles
      }));
    }
  };



  const handleSaveEditedUserButton = () => {
    setEditRow(null)
    setAddRowOpen(false)

    if (
      editedIsAdmin !==
      (Array.isArray(accounts[editRow].roles) &&
        accounts[editRow].roles.indexOf('Admin') >= 0)
    ) {
      const updatedRoles = accounts[editRow].roles;
      if (editedIsAdmin === true) {
        if (updatedRoles.indexOf('User') > -1) {
          updatedRoles.splice(updatedRoles.indexOf('User'), 1);
        }
        updatedRoles.push('Admin');
      } else if (editedIsAdmin === false) {
        if (updatedRoles.indexOf('Admin') > -1) {
          updatedRoles.splice(updatedRoles.indexOf('Admin'), 1);
        }
        updatedRoles.push('User');
      }
      dispatch(associateRolesWithAccount(
        accounts[editRow].id,
        updatedRoles
      ));
    }
  };

  const handleConfirmDeleteAccountButton = () => {
    dispatch(deleteAccount(
      accounts[accountToDeleteIndex].id
    ))
    setAccountToDeleteIndex(null)
    setModalOpen(false)
  };

  const emailAddressInUse = () => {
    return accountEmailAddressesInUse.indexOf(editedEmailAddress) > -1 &&
      accountEmailAddressesInUse.indexOf(editedEmailAddress) !== editRow;
  }

  const closeModal = () => {
    setModalOpen(false)
    setAccountToDeleteIndex(null)
  }


  const validateEditedUserAccount = () => {
    let valid = (
      !emailAddressInUse() &&
      checkEmailAddress(editedEmailAddress) &&
      editedFirstName !== '' &&
      editedLastName !== ''
    );

    if (!addRow) {
      const originalAccount = accounts[editRow];
      valid = valid && (editedFirstName !== originalAccount.user.firstName ||
        editedLastName !== originalAccount.user.lastName ||
        editedEmailAddress !== originalAccount.username ||
        editedIsAdmin !==
        (Array.isArray(originalAccount.roles) &&
          originalAccount.roles.indexOf('Admin') >= 0));
    }
    return valid;
  };

  const handleEditButton = (index) => {
    setEditRow(index);
    setAddRowOpen(false);
    setEditedFirstName(accounts[index].user.firstName)
    setEditedLastName(accounts[index].user.lastName)
    setEditedEmailAddress(accounts[index].username)
    setEditedIsAdmin(Array.isArray(accounts[index].roles) &&
      accounts[index].roles.indexOf('Admin') >= 0);
    setEmailAddressValid(checkEmailAddress(accounts[index].username))
  }


  const renderLastColumn = (account, index) => {
    if (Array.isArray(account.scopes) && account.scopes.indexOf('owner') >= 0) {
      return <React.Fragment>(Owner)</React.Fragment>;
    } else if (
      Array.isArray(account.roles) &&
      account.roles.indexOf('Admin') >= 0
    ) {
      return (
        <React.Fragment>
          <button
            type="button"
            className="UserAccounts_icon-button"
            onClick={() => handleEditButton(index)}
          >
            <span className="screen-reader">edit</span>
            <SVGIcon icon="edit" />
          </button>{' '}
          (Admin)
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <button
          type="button"
          className="UserAccounts_icon-button"
          onClick={() => handleEditButton(index)}
        >
          <span className="screen-reader">edit</span>
          <SVGIcon icon="edit" />
        </button>{' '}
        <button
          type="button"
          className="UserAccounts_icon-button"
          onClick={() => handleDeleteButton(index)}
        >
          <span className="screen-reader">delete</span>
          <SVGIcon icon="delete" />
        </button>
      </React.Fragment>
    );
  };

  return (
    <div className="UserAccounts container">
      <div className="Account_section-title-row">
        <h3 className="Account_section-title-row_title">
          {clientName} - User Accounts
        </h3>
        <Button
          label="Add User"
          onClick={() => handleAddUserButton()}
          color="link"
        />
      </div>
      <LoadingContainer
        className="Account_personal-details content-card"
        loading={loading}
      >
        <table className="UserAccounts_table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th />
            </tr>
          </thead>
          <tbody>
          {addRow && (
              <UserAccountsEditRow
              type="add"
                firstName={editedFirstName}
                firstNameOnChange={e =>setEditedFirstName(e.target.value)}
                lastName={editedLastName}
                lastNameOnChange={e => setEditedLastName(e.target.value)}
                email={editedEmailAddress}
                emailOnChange={e => handleEmailOnChange(e)}
                handleCancelButton={(e) => handleCancelButton(e)}
                handleSaveButton={() => handleSaveAddUserButton()}
                emailAddressValid={emailAddressValid}
                isAdmin={editedIsAdmin}
                handleAdminCheckbox={() => setEditedIsAdmin(!editedIsAdmin)}
                emailAddressInUse={emailAddressInUse()}
                inputValid={() => validateEditedUserAccount()}
              />
            )}

            {accounts.map((account, index) =>
              index === editRow ? (
                <UserAccountsEditRow
                  key={account.id}
                  type="edit"
                  firstName={editedFirstName}
                  firstNameOnChange={e =>setEditedFirstName(e.target.value)}
                  lastName={editedLastName}
                  lastNameOnChange={e => setEditedLastName(e.target.value)}
                  email={editedEmailAddress}
                  emailOnChange={e => handleEmailOnChange(e)}
                  handleCancelButton={(e) => handleCancelButton(e)}
                  handleSaveButton={() => handleSaveEditedUserButton()}
                  emailAddressValid={emailAddressValid}
                  isAdmin={editedIsAdmin}
                  handleAdminCheckbox={() => setEditedIsAdmin(!editedIsAdmin)}
                  emailAddressInUse={emailAddressInUse()}
                  inputValid={() => validateEditedUserAccount()}
                />
              ) : (
                  <tr key={account.id}>
                    <td>{account.user.firstName}</td>
                    <td>{account.user.lastName}</td>
                    <td>{account.username}</td>
                    <td>{renderLastColumn(account, index)}</td>
                  </tr>
                )
            )}

          </tbody>
        </table>
      </LoadingContainer>
      <Modal isOpen={modalOpen} toggle={() => closeModal()} centered>
        <ModalHeader toggle={() => closeModal()}>
          Delete User Account
        </ModalHeader>
        {(accountToDeleteIndex || accountToDeleteIndex === 0) && (
          <ModalBody>
            <p>
              Are you sure you want to delete account{' '}
              {accounts[accountToDeleteIndex].username}?
            </p>
            <p>This can not be undone.</p>
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" onClick={() => closeModal()}>
            Cancel
          </Button>{' '}
          <Button onClick={() => handleConfirmDeleteAccountButton()}>
            Delete Account
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}


UserAccounts.defaultProps = {
  loading: false,
};

export default UserAccounts;
